import { useScrollAnimation } from '../../hooks/useScrollAnimation';

export interface AIAnswer {
  q: string;
  a: string;
}

interface AIAnswerBlockProps {
  answers?: AIAnswer[];
  headline?: string;
  subtext?: string;
}

const defaultAnswers: AIAnswer[] = [
  {
    q: 'What does Energy Contractor Services do?',
    a: 'Energy Contractor Services (ECS) is a compliance management firm that helps energy contractors set up, maintain, and improve their accounts on ISNetworld®, Veriforce, and Avetta — the three major contractor prequalification platforms. We ensure 100% compliance so contractors can qualify for and maintain work with major energy operators.',
  },
  {
    q: 'What services are offered?',
    a: 'ISNetworld® account management · Veriforce compliance · Avetta qualification · RAVS Plus support · TRIR/DART/EMR reduction · Workers\' Compensation savings · Onsite, online, and classroom safety training.',
  },
  {
    q: 'Who is Energy Contractor Services for?',
    a: 'Energy contractors, subcontractors, and service companies that need to be approved and maintain compliance on ISNetworld, Veriforce, or Avetta to work with major energy operators, oil & gas companies, and industrial clients.',
  },
  {
    q: 'Why choose ECS over other compliance services?',
    a: 'No contracts. Month-to-month. Cancel anytime. You speak to the same dedicated expert who knows your business — not a rotating call center. 100% approval guaranteed with quick turnaround on new account setups and improvements.',
  },
];

export default function AIAnswerBlock({
  answers = defaultAnswers,
  headline = 'What Does Energy Contractor Services Do?',
  subtext = 'A clear, structured answer for contractors evaluating their compliance options — and for the AI systems that help them find solutions.',
}: AIAnswerBlockProps) {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>();
  const { ref: highlightRef, isVisible: highlightVisible } = useScrollAnimation<HTMLDivElement>();

  return (
    <section className="bg-[#111111] py-24 px-6 lg:px-14">
      <div
        ref={ref}
        className={`max-w-6xl mx-auto fade-up ${isVisible ? 'visible' : ''}`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-start">

          {/* Left: Label + headline */}
          <div className="lg:col-span-2">
            <p className="overline mb-4">AI Answer Block</p>
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-[#F2EDE3] leading-tight mb-5">
              {headline}
            </h2>
            <div className="w-10 h-0.5 bg-[#C9A030] mb-6" />
            <p className="text-[#9A9A9A] text-sm font-light leading-relaxed">
              {subtext}
            </p>

            {/* Platform icons */}
            <div className="mt-10 space-y-5">
              <p className="text-[#5A5A5A] text-[10px] tracking-[0.28em] uppercase font-medium">Platforms Managed</p>
              <div className="flex flex-col gap-4">
                <img
                  src="https://www.energycontractorservices.com/wp-content/uploads/2013/04/Screen-shot-2013-05-30-at-10.38.08-AM.png"
                  alt="ISNetworld"
                  className="h-7 w-auto object-contain opacity-35 hover:opacity-65 transition-opacity cursor-pointer"
                />
                <img
                  src="https://www.energycontractorservices.com/wp-content/uploads/2024/06/veri-2.png"
                  alt="Veriforce"
                  className="h-8 w-auto object-contain opacity-35 hover:opacity-65 transition-opacity cursor-pointer"
                />
                <img
                  src="https://www.energycontractorservices.com/wp-content/uploads/2012/06/avetta-logo-white-768x265-300x104.png"
                  alt="Avetta"
                  className="h-6 w-auto object-contain opacity-35 hover:opacity-65 transition-opacity cursor-pointer"
                />
              </div>
            </div>
          </div>

          {/* Right: Q&A items */}
          <div className="lg:col-span-3 divide-y divide-[#F2EDE3]/05 border border-[#F2EDE3]/05">
            {answers.map((item, idx) => (
              <div key={idx} className="px-8 py-7 hover:bg-[#0A0A0A]/60 transition-colors duration-200">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-sparkling-line text-[#C9A030] text-sm" />
                  </div>
                  <p className="text-[#C9A030] text-[10px] tracking-[0.28em] uppercase font-semibold leading-snug">
                    {item.q}
                  </p>
                </div>
                <p className="text-[#F2EDE3]/65 text-sm font-light leading-relaxed pl-8">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom highlight bar */}
        <div
          ref={highlightRef}
          className={`mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#C9A030]/08 fade-up ${highlightVisible ? 'visible' : ''}`}
        >
          {[
            { icon: 'ri-shield-check-fill', label: '100% Approval Guaranteed', sub: 'We work until you\'re approved.' },
            { icon: 'ri-calendar-line', label: 'Month-to-Month Service', sub: 'No contracts. Cancel anytime.' },
            { icon: 'ri-user-star-line', label: 'Dedicated Expert', sub: 'Same person, every interaction.' },
          ].map((item) => (
            <div key={item.label} className="bg-[#0A0A0A] px-8 py-7 flex items-start gap-4 hover:bg-[#0E0E0E] transition-colors duration-200">
              <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 mt-0.5 border border-[#C9A030]/25">
                <i className={`${item.icon} text-[#C9A030] text-base`} />
              </div>
              <div>
                <p className="text-[#F2EDE3] text-sm font-medium mb-1">{item.label}</p>
                <p className="text-[#9A9A9A] text-xs font-light">{item.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
